<template>
    <div class="version-optionals">
        <dk-toggle-dst
            v-model="showStandardOptionals"
            class="tw-mb-6"
            :class="{ 'max-lg:tw-mb-8': hasDealerSelectionLayout }"
            :checked="showStandardOptionals"
        >
            {{ $t('version-optionals.show-standard-equipment') }}
        </dk-toggle-dst>
        <template v-if="hasCategories">
            <dk-accordion-dst
                v-for="(value, title) in categories"
                v-cloak
                :key="title"
                :title="title"
                :is-large="!hasDealerSelectionLayout"
            >
                <div v-for="(valueItem, valueKey) in value" :key="valueKey" class="version-optionals__list">
                    <h6
                        v-if="valueKey.trim().toLowerCase() !== title.trim().toLowerCase()"
                        class="version-optionals__section-name tw-mt-6 tw-mb-2 tw-text-sm tw-leading-sm"
                    >
                        {{ valueKey }}
                    </h6>
                    <template v-if="showStandardOptionals || hasNonStandardOptional(valueItem)">
                        <ul>
                            <li
                                v-for="(optionalValue, optionalKey) in getFilteredOptionals(valueItem)"
                                :key="optionalKey"
                                class="tw-p-2 tw-relative"
                                :class="{
                                    'tw-bg-white': optionalKey % 2 === 0,
                                    'tw-bg-secondary-light': optionalKey % 2 !== 0,
                                }"
                            >
                                <div
                                    v-if="isEquipmentLoading(optionalValue)"
                                    class="tw-absolute tw-top-1/2 tw-left-2 -tw-translate-y-1/2 tw-mr-2"
                                >
                                    <dk-icon name="spinner" size="s" :is-spinning="true" />
                                </div>
                                <dk-checkbox
                                    id="optionalValue.id"
                                    :checked="
                                        optionalValue.isStandard ||
                                            (isSelected(optionalValue.id) && !isEquipmentLoading(optionalValue))
                                    "
                                    :disabled="isDisabled(optionalValue) || optionalValue.isStandard"
                                    :label-classes="'caption !tw-text-base-subtle tw-mr-auto'"
                                    :label="optionalValue.description"
                                    :hide-checkbox="isEquipmentLoading(optionalValue)"
                                    @change="handleOptionalClick(optionalValue)"
                                >
                                    <span
                                        class="tw-ml-4 tw-text-base-subtle tw-font-bold tw-uppercase tw-text-sm tw-leading-sm tw-shrink-0"
                                    >
                                        <template v-if="optionalValue.isStandard">
                                            {{ $t('version-optionals.standard') }}
                                        </template>
                                        <template v-else>
                                            {{ optionalValue.price | dkCurrency($i18n.locale) }}
                                        </template>
                                    </span>
                                </dk-checkbox>
                            </li>
                        </ul>
                    </template>
                    <template v-else>
                        <span class="caption tw-text-placeholder tw-leading-sm">
                            {{ $t('version-optionals.no-optionals-available') }}
                        </span>
                    </template>
                </div>
            </dk-accordion-dst>
        </template>
    </div>
</template>
<script>
import DkAccordionDst from '@v4/common/components/molecules/DkAccordionDST.vue';
import DkToggleDst from '@v4/common/components/atoms/DkToggleDST.vue';
import DkCheckbox from '@v4/common/components/molecules/DkCheckbox.vue';
import DkIcon from '@v4/common/components/atoms/DkIcon.vue';
import { addEquipment, removeEquipment } from '@v4/services/api/equipment.js';
import { dkModalAware } from '@v4/common/components/mixins/modal';
import { versionConfigurationConflict as conflictType } from '@v4/config.js';
import { dkCurrency } from '@v4/common/filters';

export default {
    name: 'VersionOptionals',
    components: {
        DkAccordionDst,
        DkToggleDst,
        DkCheckbox,
        DkIcon,
    },
    filters: {
        dkCurrency,
    },
    mixins: [dkModalAware],
    props: {
        versionId: {
            type: String,
            required: true,
        },
        categories: {
            type: Object,
            default() {
                return null;
            },
        },
        basePrice: {
            type: [Number, String],
            required: true,
        },
        hasDealerSelectionLayout: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            loadingItems: [],
            selectedOptionals: [],
            showStandardOptionals: true,
        };
    },
    computed: {
        hasCategories() {
            return !!Object.keys(this.categories || {}).length;
        },
        finalPrice() {
            return this.selectedOptionals.reduce((a, b) => a + b.price, this.basePrice);
        },
    },
    watch: {
        selectedOptionals(oldValue, newValue) {
            this.$emit('changed-optionals', newValue);
            this.$emit('changed-final-price', this.finalPrice);
        },
    },
    mounted() {
        this.$emit('changed-final-price', this.finalPrice);
    },
    methods: {
        toggleStandardOptionals() {
            this.showStandardOptionals = !this.showStandardOptionals;
        },
        hasNonStandardOptional(optionalGroup) {
            return !!Object.values(optionalGroup).find((item) => !item.isStandard);
        },
        getFilteredOptionals(optionalGroup) {
            if (this.showStandardOptionals) {
                return optionalGroup;
            }

            return Object.values(optionalGroup).filter((item) => !item.isStandard);
        },
        isAddOneOfConflict(response) {
            const { alternativesAsEquipmentIdTree } = response.data;
            return (
                alternativesAsEquipmentIdTree !== undefined && alternativesAsEquipmentIdTree.ADD_ONE_OF !== undefined
            );
        },
        isAddAllConflict(response) {
            const { alternativesAsEquipmentIdTree } = response.data;
            return alternativesAsEquipmentIdTree !== undefined && alternativesAsEquipmentIdTree.ADD_ALL !== undefined;
        },
        isAddOneAddAllConflict(response) {
            const { alternativesAsEquipmentIdTree } = response.data;
            return (
                alternativesAsEquipmentIdTree !== undefined &&
                alternativesAsEquipmentIdTree.ADD_ALL !== undefined &&
                alternativesAsEquipmentIdTree.ADD_ONE_OF !== undefined
            );
        },
        isRemoveOneOfConflict(response) {
            const { alternativesAsEquipmentIdTree } = response.data;
            return (
                alternativesAsEquipmentIdTree !== undefined && alternativesAsEquipmentIdTree.REMOVE_ONE_OF !== undefined
            );
        },
        isRemoveAllConflict(response) {
            const { alternativesAsEquipmentIdTree } = response.data;
            return (
                alternativesAsEquipmentIdTree !== undefined && alternativesAsEquipmentIdTree.REMOVE_ALL !== undefined
            );
        },
        getOptionalUniqueId({ id, description, cluster }) {
            return `${cluster}-${id}-${description}`;
        },
        handleConflict(optional, data, type) {
            return new Promise((resolve, reject) => this.showConflictModal(optional, data, type, resolve, reject));
        },
        showConflictModal(optional, data, type, resolve, reject) {
            return import(
                /* webpackChunkName: "VersionOptionalsModal-vue" */ '@v4/multi-section/components/molecules/VersionOptionalsModal.vue'
            ).then(({ default: VersionOptionalsModal }) => {
                this.showModal(
                    VersionOptionalsModal,
                    {
                        optional,
                        data,
                        type,
                        handleModify: resolve,
                        handleClose: reject,
                    },
                    {
                        name: `version-optionals-modal-${type}-${optional.id}-${Date.now()}`,
                        height: 'auto',
                        classes: 'modal--fullscreen_medium',
                        // Do not close modal on background click or we can sit in an incoherent state.
                        clickToClose: false,
                    },
                    {},
                    {
                        disableBodyScroll: false,
                    }
                );
            });
        },
        isSelected(id) {
            return this.selectedOptionals.find((x) => String(x.id) === String(id)) !== undefined;
        },
        isDisabled(optional) {
            return !this.isEquipmentLoading(optional) && this.loadingItems.length > 0;
        },
        isEquipmentLoading(optional) {
            const uniqueId = this.getOptionalUniqueId(optional);
            return this.loadingItems.find((x) => x === uniqueId) !== undefined;
        },
        addLoadingId(id) {
            this.loadingItems.push(id);
        },
        removeLoadingId(id) {
            const index = this.loadingItems.indexOf(id);
            if (index > -1) {
                this.loadingItems.splice(index, 1);
            }
        },
        addOptionalDefinitively(optionals) {
            /**
             * We add only optionals NOT in selectedOptionals list.
             */
            this.selectedOptionals.push(
                ...optionals.filter(
                    (item) => this.selectedOptionals.findIndex((el) => String(el.id) === String(item.id)) === -1
                )
            );
            optionals.forEach((optional) => {
                const uniqueId = this.getOptionalUniqueId(optional);
                this.removeLoadingId(uniqueId);
            });
        },
        async addOptional(optional, config = null) {
            const uniqueId = this.getOptionalUniqueId(optional);
            this.addLoadingId(uniqueId);

            try {
                const c = config === null ? this.selectedOptionals.map((item) => item.id).join() : config;
                const response = await addEquipment(this.versionId, optional.id, c);

                if (response.success) {
                    this.addOptionalDefinitively(response.data.equipmentDTOs);
                } else {
                    if (response.data) {
                        this.handleResponseConflict(optional, config, response, 'ADD');
                    }
                }
                this.removeLoadingId(uniqueId);
            } catch (e) {
                // @TODO Handle error
            }
        },
        removeItemFromSelectedOptionals(optional) {
            const index = this.selectedOptionals.findIndex((x) => String(x.id) === String(optional.id));
            if (index > -1) {
                this.selectedOptionals.splice(index, 1);
                const uniqueId = this.getOptionalUniqueId(optional);
                this.removeLoadingId(uniqueId);
            }
        },
        removeOptionalDefinitively(optional, shouldForceRemoveDependentOptional = false) {
            this.removeItemFromSelectedOptionals(optional);

            if (shouldForceRemoveDependentOptional) {
                const parentId = optional.id;
                const dependentOptionals = this.selectedOptionals.filter(
                    (opt) => opt.parents.indexOf(parentId) > -1 || opt.parents.indexOf(String(parentId)) > -1
                );
                if (dependentOptionals.length > 0) {
                    dependentOptionals.forEach((item) => this.removeItemFromSelectedOptionals(item));
                }
            }
        },
        async removeOptional(optional, config = null) {
            const uniqueId = this.getOptionalUniqueId(optional);
            this.addLoadingId(uniqueId);

            try {
                const c = config === null ? this.selectedOptionals.map((item) => item.id).join() : config;
                const response = await removeEquipment(this.versionId, optional.id, c);

                if (response.success) {
                    await this.removeOptionalDefinitively(optional, true);
                } else {
                    if (response.data) {
                        if (this.isRemoveAllConflict(response)) {
                            this.handleConflict(optional, response.data, conflictType.REMOVE_ALL).then((picked) => {
                                const tempConfig = this.selectedOptionals
                                    .map((item) => item.id)
                                    .filter((id) => picked.map((item) => item.id).indexOf(id) === -1);
                                picked.forEach((item) => {
                                    this.removeOptionalDefinitively(item);
                                });
                                this.removeOptional(optional, tempConfig.join());
                            });
                        }
                    }

                    this.removeLoadingId(uniqueId);
                }
            } catch (e) {
                // @TODO Handle error
            }
        },
        handleOptionalClick(optionalValue = {}) {
            if (optionalValue.isStandard) {
                return;
            }

            if (!this.isSelected(optionalValue.id) && !this.isEquipmentLoading(optionalValue)) {
                this.addOptional(optionalValue);
            } else if (this.isSelected(optionalValue.id) && !this.isEquipmentLoading(optionalValue)) {
                this.removeOptional(optionalValue);
            }
        },
        handleResponseConflict(optional, config, response, type = 'ADD') {
            if (this.isAddOneAddAllConflict(response)) {
                this.handleAddOneOfAddAllConflict(optional, response);
            } else if (this.isAddOneOfConflict(response)) {
                this.handleAddOneOfConflict(optional, response);
            } else if (this.isAddAllConflict(response)) {
                this.handleAddAllConflict(optional, response);
            } else if (this.isRemoveAllConflict(response)) {
                this.handleRemoveAllConflict(optional, response);
            } else if (this.isRemoveOneOfConflict(response)) {
                this.handleRemoveOneOfConflict(optional, response);
            } else {
                if (type === 'ADD') {
                    this.addOptionalDefinitively([optional].concat(config.split(',')));
                }
            }
        },
        handleAddAllConflict(optional, { data }) {
            this.handleConflict(optional, data, conflictType.ADD_ALL).then(
                (picked) => {
                    const tempConfig = [...picked, ...data.equipmentDTOs.map((item) => item.id)].filter(
                        (value, index, self) => self.indexOf(value) === index
                    );
                    if (data.config !== '') {
                        tempConfig.push(data.config);
                    }
                    this.addOptional(optional, tempConfig.join());
                },
                () => {}
            );
        },
        handleAddOneOfConflict(optional, { data }) {
            this.handleConflict(optional, data, conflictType.ADD_ONE_OF).then(
                (picked) => {
                    const tempConfig = [...picked, ...this.selectedOptionals.map((item) => item.id)].filter(
                        (value, index, self) => self.indexOf(value) === index
                    );
                    if (data.config !== '') {
                        tempConfig.push(data.config);
                    }
                    this.addOptional(optional, tempConfig.join());
                },
                () => {}
            );
        },
        handleAddOneOfAddAllConflict(optional, { data }) {
            this.handleConflict(optional, data, conflictType.ADD_ONE_ALL).then(
                (picked) => {
                    const tempConfig = [...picked, ...this.selectedOptionals.map((item) => item.id)].filter(
                        (value, index, self) => self.indexOf(value) === index
                    );
                    this.addOptional(optional, tempConfig.join());
                },
                () => {}
            );
        },
        handleRemoveAllConflict(optional, { data }) {
            this.handleConflict(optional, data, conflictType.REMOVE_ALL).then(
                (picked) => {
                    const tempConfig = this.selectedOptionals
                        .map((item) => item.id)
                        .filter((id) => picked.map((item) => item.id).indexOf(id) === -1);
                    picked.forEach(async (pickedItem) => {
                        await this.removeOptional(pickedItem, this.selectedOptionals.map((item) => item.id).join());
                    });
                    this.addOptional(optional, tempConfig.join());
                },
                () => {}
            );
        },
        handleRemoveOneOfConflict(optional, { data }) {
            this.handleConflict(optional, data, conflictType.REMOVE_ONE_OF).then(
                async (picked) => {
                    await this.removeOptional(picked[0]);
                    this.addOptional(optional);
                },
                () => {}
            );
        },
    },
};
</script>
